// CF-ZH-L10N-V1 — React i18n layer. Thin wrapper over the pure functions in core.ts.
// Auto-detects Chinese from the Telegram Mini App user's language_code at boot;
// no manual toggle. English for everyone else. EN fallback for any untranslated key.
//
// CF-DRAGONTIGER-SEPARATE-APP-V1: under the `dragontiger` build variant the
// language is FORCED to zh (the Chinese 金龙虎 app), bypassing language_code
// detection. The default cricketflip build is unchanged (Telegram auto-detect).
// CF-GERMAN-VARIANT-V1: under the `german` build variant the language is FORCED
// to de (the „Kopf oder Zahl" app), same way. Forced-language is added ONLY on
// the branded-variant paths — the default/dragontiger paths are untouched (no
// German auto-detect is ever introduced into Telegram language_code detection).
import React, { createContext, useContext, useEffect, useMemo } from 'react';
import { Lang, Dict, Vars, pickLang, resolve } from './core';
import { en } from './en';
import { zh } from './zh';
import { de } from './de';
import { pt } from './pt';
import { isDragonTiger, isGerman, isBrazil, BRAND_NAME } from '../lib/app-variant';

const DICTS: Record<Lang, Dict> = { en, zh, de, pt };

/**
 * Resolve the UI language once.
 * - dragontiger variant → always 'zh' (forced; ignores Telegram language_code).
 * - german variant      → always 'de' (forced; ignores Telegram language_code).
 * - default variant     → Telegram user's language_code via pickLang; en otherwise.
 * Safe on web (no Telegram) → en.
 */
function detectLang(): Lang {
  if (isDragonTiger) return 'zh';
  if (isGerman) return 'de';
  if (isBrazil) return 'pt';
  try {
    const code = (window as { Telegram?: { WebApp?: { initDataUnsafe?: { user?: { language_code?: unknown } } } } })
      .Telegram?.WebApp?.initDataUnsafe?.user?.language_code;
    return pickLang(code);
  } catch {
    return 'en';
  }
}

export type TFn = (key: string, vars?: Vars) => string;

// Module-level current language for NON-React code (lib utilities that can't use
// the useT hook — e.g. lib/api.ts global 401 popup, lib/deposit-amount.ts error
// strings, lib/ui-derivers.ts). Resolved at module load and re-synced when
// LangProvider mounts. language_code is fixed per session, so this is safe.
let currentLang: Lang = detectLang();

// CF-JINLONGHU-BRAND-LEAK-V1 (PL #8640): the 金龙虎 build must never show the
// English "CricketFlip" product name. The zh/en dictionaries are generated +
// Scorer-owned and still contain "CricketFlip"; we rewrite it to the variant
// brand at the SINGLE resolve boundary so no dictionary string — present or
// future, including the lib/api.ts session-timeout popup that goes through
// translate() — can leak the English brand to Chinese players. Default build:
// identity (isDragonTiger=false), so default RUNTIME BEHAVIOR is unchanged (the
// bundle hash changes from the added code, but brandize folds to a no-op).
// Case-sensitive: the lowercase @cricketflip_bot / @cricketflip_official handles
// are deliberately NOT rewritten. They are the REAL primary-auth operational
// handles reused by the branded builds (no per-variant bot yet — a German/zh bot
// is a separate ticket); brandize only folds the English PRODUCT NAME "CricketFlip".
// CF-GERMAN-VARIANT-V1 R2: these handles are an explicit, documented allowlist in
// scripts/i18n-check.mts (de brand-clean test); any OTHER 'cricketflip' (any case)
// in a de value fails CI.
//
// CF-JINLONGHU-LEGAL-LEAK-V1 (sweep B-2): exported so the /terms + /privacy
// pages — which render raw TERMS_TEXT/PRIVACY_TEXT constants directly, bypassing
// the dictionary resolve() path — reuse this ONE canonical brand-rewrite instead
// of duplicating the regex. Identity for the default build (unchanged behavior).
//
// CF-GERMAN-VARIANT-V1: the „Kopf oder Zahl" build must ALSO never surface the
// English "CricketFlip" name. The de dictionary is authored brand-clean, but this
// rewrite is the same single-boundary belt-and-suspenders the 金龙虎 build relies
// on (it also covers any non-dictionary string passing through translate(), e.g.
// future shared constants). Identity for the default build (unchanged behavior).
export function brandize(text: string): string {
  return isDragonTiger || isGerman || isBrazil ? text.replace(/CricketFlip/g, BRAND_NAME) : text;
}

/** Non-hook translation for use OUTSIDE React components (lib/*). Components use useT(). */
export function translate(key: string, vars?: Vars): string {
  return brandize(resolve(currentLang, DICTS, key, vars));
}

const LangContext = createContext<Lang>('en');

export function LangProvider({ children }: { children: React.ReactNode }) {
  // Detect once at mount; language_code does not change within a session.
  const lang = useMemo(() => {
    currentLang = detectLang();
    return currentLang;
  }, []);

  // CF-DRAGONTIGER-SEPARATE-APP-V1: activate the red/gold palette ONCE at boot,
  // but ONLY under the dragontiger build variant. The default cricketflip app
  // never sets data-theme (no [data-theme] rules apply → untouched gold theme).
  // CF-GERMAN-VARIANT-V1: activate the black/gold „Kopf oder Zahl" palette ONCE
  // at boot, ONLY under the german build variant (data-theme="german"). Mirror
  // the dragontiger activation. Variant-gated, NOT language-gated.
  useEffect(() => {
    if (isDragonTiger) {
      document.documentElement.dataset.theme = 'dragon-tiger';
    } else if (isGerman) {
      document.documentElement.dataset.theme = 'german';
    } else if (isBrazil) {
      document.documentElement.dataset.theme = 'brazil';
    }
  }, []);

  return <LangContext.Provider value={lang}>{children}</LangContext.Provider>;
}

/** Current resolved UI language ('en' | 'zh'). */
export function useLang(): Lang {
  return useContext(LangContext);
}

/** Translation function: t('some.key', { amount: 5 }). */
export function useT(): TFn {
  const lang = useContext(LangContext);
  return useMemo<TFn>(() => (key, vars) => brandize(resolve(lang, DICTS, key, vars)), [lang]);
}
